import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { CallbackRequest } from '@limo/database'
import { formatDistanceToNow, format } from 'date-fns'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import {
  PhoneIncoming,
  MoreHorizontal,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  Calendar,
  XCircle,
  MessageSquare,
} from 'lucide-react'

type CallbackStatus = 'pending' | 'scheduled' | 'completed' | 'cancelled'

const statusConfig: Record<
  CallbackStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  pending: { label: 'Pending', variant: 'default' },
  scheduled: { label: 'Scheduled', variant: 'secondary' },
  completed: { label: 'Completed', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
}

export function CallbacksPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [statusFilter, setStatusFilter] = useState<CallbackStatus | 'all'>('all')
  const [selectedCallback, setSelectedCallback] = useState<CallbackRequest | null>(null)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch callback requests
  const { data: callbacks, isLoading } = useQuery({
    queryKey: ['callbacks', activeAccount?.id, statusFilter],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      let query = supabase
        .from('callback_requests')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      const { data, error } = await query
      if (error) throw error
      return data as CallbackRequest[]
    },
    enabled: !!activeAccount?.id,
  })

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: CallbackStatus }) => {
      const { error } = await supabase
        .from('callback_requests')
        .update({ status })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['callbacks'] })
      toast({
        title: 'Status updated',
        description: `Callback marked as ${status}.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a')
    } catch {
      return dateStr
    }
  }

  const formatRelativeDate = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Callback Requests</h1>
          <p className="text-muted-foreground">
            Manage callback requests from chat conversations
          </p>
        </div>
      </div>

      {/* Status filter tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : !callbacks || callbacks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <PhoneIncoming className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No callback requests</h3>
          <p className="text-muted-foreground max-w-md">
            {statusFilter === 'all'
              ? 'Callback requests from chat conversations will appear here.'
              : `No ${statusFilter} callback requests found.`}
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="hidden md:table-cell">Contact</TableHead>
                <TableHead className="hidden lg:table-cell">Preferred Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell">Requested</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {callbacks.map((callback) => (
                <TableRow key={callback.id}>
                  <TableCell>
                    <div className="font-medium">{callback.customer_name}</div>
                    {callback.reason && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {callback.reason}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex flex-col gap-1">
                      <a
                        href={`tel:${callback.customer_phone}`}
                        className="flex items-center gap-1 text-sm hover:underline"
                      >
                        <Phone className="h-3 w-3" />
                        {callback.customer_phone}
                      </a>
                      {callback.customer_email && (
                        <a
                          href={`mailto:${callback.customer_email}`}
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:underline"
                        >
                          <Mail className="h-3 w-3" />
                          {callback.customer_email}
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {callback.preferred_time_text || formatDate(callback.preferred_time) || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusConfig[callback.status as CallbackStatus].variant}>
                      {statusConfig[callback.status as CallbackStatus].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">
                    {formatRelativeDate(callback.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedCallback(callback)}>
                          <MessageSquare className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {callback.status !== 'scheduled' && (
                          <DropdownMenuItem
                            onClick={() =>
                              updateStatusMutation.mutate({
                                id: callback.id,
                                status: 'scheduled',
                              })
                            }
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            Mark as Scheduled
                          </DropdownMenuItem>
                        )}
                        {callback.status !== 'completed' && (
                          <DropdownMenuItem
                            onClick={() =>
                              updateStatusMutation.mutate({
                                id: callback.id,
                                status: 'completed',
                              })
                            }
                          >
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Mark as Completed
                          </DropdownMenuItem>
                        )}
                        {callback.status !== 'cancelled' && (
                          <DropdownMenuItem
                            onClick={() =>
                              updateStatusMutation.mutate({
                                id: callback.id,
                                status: 'cancelled',
                              })
                            }
                            className="text-destructive"
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selectedCallback} onOpenChange={() => setSelectedCallback(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Callback Request Details</DialogTitle>
            <DialogDescription>
              Request from {selectedCallback?.customer_name}
            </DialogDescription>
          </DialogHeader>
          {selectedCallback && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedCallback.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge variant={statusConfig[selectedCallback.status as CallbackStatus].variant}>
                    {statusConfig[selectedCallback.status as CallbackStatus].label}
                  </Badge>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Contact</p>
                <div className="space-y-1">
                  <a
                    href={`tel:${selectedCallback.customer_phone}`}
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {selectedCallback.customer_phone}
                  </a>
                  {selectedCallback.customer_email && (
                    <a
                      href={`mailto:${selectedCallback.customer_email}`}
                      className="flex items-center gap-2 text-muted-foreground hover:underline"
                    >
                      <Mail className="h-4 w-4" />
                      {selectedCallback.customer_email}
                    </a>
                  )}
                </div>
              </div>

              {(selectedCallback.preferred_time_text || selectedCallback.preferred_time) && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Preferred Time</p>
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {selectedCallback.preferred_time_text || formatDate(selectedCallback.preferred_time)}
                  </p>
                </div>
              )}

              {selectedCallback.reason && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Reason</p>
                  <p>{selectedCallback.reason}</p>
                </div>
              )}

              {selectedCallback.notes && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Notes</p>
                  <p className="text-muted-foreground">{selectedCallback.notes}</p>
                </div>
              )}

              <div className="text-sm text-muted-foreground">
                Requested {formatRelativeDate(selectedCallback.created_at)}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="default"
                  className="flex-1"
                  asChild
                >
                  <a href={`tel:${selectedCallback.customer_phone}`}>
                    <Phone className="mr-2 h-4 w-4" />
                    Call Now
                  </a>
                </Button>
                {selectedCallback.status === 'pending' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      updateStatusMutation.mutate({
                        id: selectedCallback.id,
                        status: 'completed',
                      })
                      setSelectedCallback(null)
                    }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Mark Complete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
